import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  actions: { display: "flex", gap: 4 },
}));

const EMPTY_FORM = { name: "", email: "", password: "", companyId: "", whitelabelScopeId: "" };

// feat_237: presets de permisos (espejo del backend).
const PRESETS = {
  standard: ["panel_general", "changelog"],
  turnosia: ["panel_general", "turnosIA", "citas", "changelog"],
};

const SuperAdminUsersManager = () => {
  const classes = useStyles();
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [whitelabels, setWhitelabels] = useState([]);
  const [featuresCatalog, setFeaturesCatalog] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [{ data: sa }, { data: overview }, { data: wls }, { data: cat }] = await Promise.all([
        api.get("/superadmin/superadmins"),
        api.get("/superadmin/overview"),
        api.get("/superadmin/whitelabels-brief"),
        api.get("/superadmin/features-catalog"),
      ]);
      setUsers(sa || []);
      setCompanies(overview.companies || []);
      setWhitelabels(wls || []);
      setFeaturesCatalog(cat || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setSelectedFeatures(new Set(PRESETS.standard));
    setEditing({ new: true });
  };

  const openEdit = (u) => {
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      companyId: u.companyId || "",
      whitelabelScopeId: u.whitelabelScopeId || "",
    });
    // superadminPermissions puede venir como array o string (defensivo).
    let perms = u.superadminPermissions;
    if (typeof perms === "string") {
      try { perms = JSON.parse(perms); } catch { perms = []; }
    }
    setSelectedFeatures(new Set(Array.isArray(perms) ? perms : []));
    setEditing(u);
  };

  const applyPreset = (name) => {
    if (name === "full") {
      setSelectedFeatures(
        new Set(featuresCatalog.filter((f) => !f.superOnly).map((f) => f.key))
      );
    } else {
      setSelectedFeatures(new Set(PRESETS[name] || []));
    }
  };

  const toggleFeature = (key, checked) => {
    setSelectedFeatures((prev) => {
      const s = new Set(prev);
      if (checked) s.add(key);
      else s.delete(key);
      return s;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // feat_237: permisos solo aplican a resellers (con marca).
      const superadminPermissions = form.whitelabelScopeId
        ? Array.from(selectedFeatures)
        : null;
      if (editing.new) {
        await api.post("/superadmin/superadmins", { ...form, superadminPermissions });
        toast.success("Superadmin creado");
      } else {
        const payload = { ...form, superadminPermissions };
        if (!payload.password) delete payload.password;
        await api.put(`/superadmin/superadmins/${editing.id}`, payload);
        toast.success("Superadmin actualizado");
      }
      setEditing(null);
      fetchData();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar superadmin ${u.email}?`)) return;
    try {
      await api.delete(`/superadmin/superadmins/${u.id}`);
      toast.success("Superadmin eliminado");
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <Typography variant="h6">Superadmins</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Crear superadmin
        </Button>
      </div>

      <Paper style={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              const isLast = users.length <= 1;
              return (
                <TableRow key={u.id}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    {u.companyId == null ? (
                      <em style={{ color: "#9ca3af" }}>Sin empresa</em>
                    ) : (
                      companies.find((c) => c.id === u.companyId)?.name || u.companyId
                    )}
                  </TableCell>
                  <TableCell>
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className={classes.actions}>
                    <Tooltip title="Editar">
                      <IconButton size="medium" onClick={() => openEdit(u)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        isSelf
                          ? "No podés borrarte a vos mismo"
                          : isLast
                          ? "No podés borrar el último superadmin"
                          : "Eliminar"
                      }
                    >
                      <span>
                        <IconButton
                          size="medium"
                          onClick={() => handleDelete(u)}
                          disabled={isSelf || isLast}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing?.new ? "Crear superadmin" : `Editar ${editing?.email}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, name: v }));
            }}
          />
          <TextField
            label="Email"
            fullWidth
            margin="dense"
            type="email"
            value={form.email}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, email: v }));
            }}
          />
          <TextField
            label={editing?.new ? "Password" : "Password (dejar vacío para no cambiar)"}
            fullWidth
            margin="dense"
            type="password"
            value={form.password}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, password: v }));
            }}
          />
          <TextField
            select
            label="Empresa asignada (opcional)"
            fullWidth
            margin="dense"
            value={form.companyId}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, companyId: v }));
            }}
          >
            <MenuItem value="">
              <em>Sin empresa (superadmin puro)</em>
            </MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          {/* feat_236: scope de marca blanca (reseller) */}
          <TextField
            select
            label="Marca (whitelabel scope)"
            fullWidth
            margin="dense"
            value={form.whitelabelScopeId}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, whitelabelScopeId: v }));
            }}
            helperText="Vacío = super superadmin (ve todo). Con marca = reseller: solo ve/crea empresas de esa marca y no gestiona otros superadmins."
          >
            <MenuItem value="">
              <em>Sin scope (super superadmin)</em>
            </MenuItem>
            {whitelabels.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>

          {/* feat_238 (F1): aclaración de los 3 tipos de superadmin */}
          <Alert severity="info" style={{ marginTop: 12 }}>
            <strong>Tipos de superadmin:</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              <li>Super superadmin: sin marca y sin permisos limitados → acceso total.</li>
              <li>Reseller de marca: con marca → solo ve empresas de esa marca.</li>
              <li>Reseller de WAIA base: sin marca + con permisos limitados → ve empresas creadas por él.</li>
            </ul>
          </Alert>

          {/* feat_237/238: permisos del reseller. Si tiene marca o si se destildan
              permisos sin marca (WAIA base), aplican. */}
          {form.whitelabelScopeId && (
            <div style={{ marginTop: 16 }}>
              <Divider />
              <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                Permisos del reseller
              </Typography>
              <div style={{ display: "flex", gap: 8, margin: "8px 0", flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" onClick={() => applyPreset("standard")}>
                  Reseller estándar
                </Button>
                <Button size="small" variant="outlined" onClick={() => applyPreset("turnosia")}>
                  TurnosIA only
                </Button>
                <Button size="small" variant="outlined" onClick={() => applyPreset("full")}>
                  Full
                </Button>
              </div>
              <FormGroup>
                {featuresCatalog
                  .filter((f) => !f.superOnly)
                  .map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedFeatures.has(f.key)}
                          onChange={(e) => toggleFeature(f.key, e.target.checked)}
                        />
                      }
                      label={
                        <span>
                          <strong>{f.label}</strong>
                          {f.scoped && (
                            <span style={{ color: "#2e7d32", fontSize: 11 }}> · scoped</span>
                          )}
                          <br />
                          <small style={{ color: "gray" }}>{f.description}</small>
                        </span>
                      }
                    />
                  ))}
              </FormGroup>
              <Typography variant="caption" color="textSecondary">
                Marca Blanca, Resellers y Superadmins nunca están disponibles para un reseller.
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperAdminUsersManager;
